import React, { useEffect, useState } from "react";
import axios from "axios";
import { SpinLoad } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { CompanyContact } from "@bindeleddet/core/types";
import { List, Empty, Button, Row } from "antd";
import CompanyContactBox from "./CompanyContactBox/CompanyContactBox";
import CreateOrEditContact from "./CompanyContactBox/CreateOrEditContact";

interface Props {
  companyId: number;
  onNewMain?: (contact: CompanyContact | undefined) => void;
}

const CompanyContacts: React.FC<Props> = ({ companyId, onNewMain }) => {
  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [mainLoading, setMainLoading] = useState<boolean>(false);
  const [showCreateContactModal, setShowCreateContactModal] = useState<boolean>(false);

  useEffect(() => {
    axios
      .get<CompanyContact[]>(`/companies/${companyId}/contact-persons/`)
      .then(response => {
        setContacts(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [companyId]);

  const handleMain = async (changingContact: CompanyContact) => {
    /*
      Since the system is designed to only allow one main contact per company, this logic could have been simpler.
      But a loop hole from "Atlas" made it possible to have multiple, so we have some extra checks and handlers.
      We also try to make it impossible to have no main contact, by not allowing deletion of the last main contact.
      But also here the old page had a loop hole, so we have to assume that there could be no main contacts.
    */
    setMainLoading(true);
    if (changingContact.main_contact && contacts.filter(contact => contact.main_contact).length === 1) {
      setMainLoading(false);
      return message("error", "Feil under oppdatering", "Bedriften må ha en hovedkontakt.");
    }

    try {
      const putResponse = await axios.put<CompanyContact[]>(
        `/companies/${companyId}/contact-persons/${changingContact.id}/`,
        { ...changingContact, main_contact: !changingContact.main_contact }
      );
      if (putResponse.status === 200) {
        // Find already existing main contacts
        const mainContact = contacts.find(contact => contact.main_contact && contact.id !== changingContact.id);
        if (mainContact && !changingContact.main_contact) {
          // If the changng contact becomes a main contact, the previous main contact should be set to not main contact
          await axios.put(`/companies/${companyId}/contact-persons/${mainContact.id}/`, {
            ...mainContact,
            main_contact: false,
          });
        }
        message("success", "Hovedkontakt oppdatert");
        setContacts(prevContacts => {
          const updatedContacts = prevContacts.map(contact => {
            if (contact.id === changingContact.id) {
              // Update the changing contact to opposite of what it was
              return {
                ...contact,
                main_contact: !changingContact.main_contact,
              };
            }
            if (contact.id === mainContact?.id && !changingContact.main_contact) {
              // If the changing contact is becoming main contact, the previous main contact should be set to not main contact
              return {
                ...contact,
                main_contact: false,
              };
            }
            return contact;
          });
          onNewMain && onNewMain(updatedContacts.find(contact => contact.main_contact));
          return updatedContacts;
        });
        setMainLoading(false);
      } else {
        message("error", "Kunne ikke oppdatere hovedkontakt");
        setMainLoading(false);
      }
    } catch (error) {
      message("error", "Kunne ikke oppdatere hovedkontakt");
      setMainLoading(false);
    }
  };

  const handleDelete = (contact: CompanyContact) => {
    if (contact.main_contact && contacts.filter(c => c.main_contact).length === 1) {
      return message(
        "error",
        "Feil under sletting",
        "Bedriften må ha en hovedkontakt. Sett en annen bedriftskontakt til hovedkontakt før du sletter denne."
      );
    }
    axios
      .delete<CompanyContact>(`/companies/${contact.company}/contact-persons/${contact.id}/`)
      .then(() => {
        message("success", "Kontaktperson slettet");
        setContacts(prevContacts => prevContacts.filter(prevContact => prevContact.id !== contact.id));
      })
      .catch(() => message("error", "Kontaktperson ikke slettet"));
  };

  const handleCreate = (contact: CompanyContact) => setContacts(prevContacts => [...prevContacts, contact]);
  const handleUpdate = (contact: CompanyContact) => {
    setContacts(prevContacts =>
      prevContacts.map(prevContact => (prevContact.id === contact.id ? contact : prevContact))
    );
  };
  const notificationBoxes = contacts.map(contact => {
    return (
      <CompanyContactBox
        key={contact.id}
        contact={contact}
        mainLoading={mainLoading}
        handleMain={() => handleMain(contact)}
        handleDelete={() => handleDelete(contact)}
        handleUpdate={updatedContact => handleUpdate(updatedContact)}
      />
    );
  });

  return (
    <>
      <Row justify="end">
        <CreateOrEditContact
          companyId={companyId}
          handleCreate={handleCreate}
          isOpen={showCreateContactModal}
          onClose={() => setShowCreateContactModal(false)}
        />
        <Button type="primary" size="large" onClick={() => setShowCreateContactModal(true)}>
          Opprett ny kontaktperson
        </Button>
      </Row>
      <List
        itemLayout="vertical"
        pagination={false}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Fant ingen kontaktpersoner" />,
        }}
        style={{ padding: 0, marginTop: 16 }}
        dataSource={notificationBoxes}
        renderItem={item => item}
        loading={{ indicator: <SpinLoad />, spinning: loading }}
      />
    </>
  );
};

export default CompanyContacts;
